import Layout from "../../../Layout";
import React, { useEffect, useMemo, useState } from "react";
import { CancelButton, SaveButton, Button } from "../../../UI/Button";
import { Link } from "../../../UI";
import { Flex, Box } from "rebass";
import PropTypes from "prop-types";
import {
  Container,
  TopContainer,
  Title,
  ContentContainer,
  ExtraOptionsSidebar,
  ExtraOptions,
  LabelLeft,
  Select,
  Radio,
  Label,
  InputGroup,
  QuestionField,
} from "./styled";
import QuestionHorizontalLayout from "./QuestionHorizontalLayout";
import QuestionVerticalLayout from "./QuestionVerticalLayout";
import { DeleteButton, DeleteButtonContainer } from "../../Navbar/styled";
import { FaTrash } from "react-icons/fa";
import ClassedUpMarkupGuide from "../../../../assets/pdf/ClassedUpMarkupGuide.pdf";
import { markupsInContent } from "../../../../utils/markup";

/**
 * @typedef {import('../../../../contexts/api/question/common').ShortAnswer} ShortAnswer;
 */

/**
 * @type {ShortAnswer[]}
 */
const defaultAnswers = () => [{ text: "" }];

const maxNumberOfAnswers = 4;

const TestLibraryQuestionsShortAnswer = ({
  shortAnswers: initialAnswers,
  content: initialContent = "",
  difficulties = [],
  difficulty: initialDifficulty = "",
  layoutOrientation: initialLayoutOrientation = "vert",
  onCancel = () => {},
  onDelete = () => {},
  onSave = () => {},
  questionNumber,
  question: initialQuestion = "",
  showButton = false,
  onNext,
  onPrevious,
}) => {
  const [question, setQuestion] = useState(initialQuestion);
  useEffect(() => {
    setQuestion(initialQuestion);
  }, [initialQuestion]);

  /**
   * parse content string, if any markup exists, store it as a key value pair.
   * initialContent = \phrase(Le Chèvre)\audio(audio-uuid)\image(image-uuid) Hello World
   * dictionary = { phrase: 'Le Chèvre', audio: 'audio-uuid', image: 'image-uuid', text: 'Hello World' }
   */
  const initialContentDictionary = useMemo(() => {
    const dictionary = markupsInContent(initialContent, [
      "image",
      "phrase",
      "video",
      "audio",
    ]);
    return dictionary;
  }, [initialContent]);

  const [contentDictionary, setContentDictionary] = useState(
    initialContentDictionary
  );

  const [difficulty, setQuestionDifficulty] = useState(initialDifficulty);
  useEffect(() => {
    setQuestionDifficulty(initialDifficulty);
  }, [initialDifficulty]);

  const [layoutOrientation, setLayoutOrientation] = useState(
    initialLayoutOrientation
  );
  useEffect(() => {
    setLayoutOrientation(initialLayoutOrientation);
  }, [initialLayoutOrientation]);

  const safeInitialAnswers = useMemo(
    () => getSafeAnswers(initialAnswers, defaultAnswers(), maxNumberOfAnswers),
    [initialAnswers]
  );

  const [answers, setAnswers] = useState(safeInitialAnswers);
  useEffect(() => {
    setAnswers(safeInitialAnswers);
  }, [safeInitialAnswers]);

  const updateContent = (newContent) => {
    // Update the content in the dictionary if key exists, otherwise add it
    // The new content could be a markup or plain text or a combination of both
    if (newContent) {
      const markups = markupsInContent(newContent, [
        "image",
        "phrase",
        "audio",
        "video",
      ]);
      setContentDictionary((current) => ({
        ...current,
        ...markups,
      }));
    }
  };

  const removeContent = (type) => {
    if (type) {
      const key = type.toLowerCase();
      // Remove the content from the dictionary if key exists
      if (contentDictionary[key]) {
        setContentDictionary((current) => {
          const newValues = { ...current };
          delete newValues[key];
          return newValues;
        });
      }
    }
  };

  const getContent = (type) => {
    if (type) {
      const key = type.toLowerCase();
      // Get the content from the dictionary if key exists
      if (contentDictionary[key]) {
        return contentDictionary[key];
      }
    }
    return "";
  };

  const handleChangeAnswerText = (index, text) => {
    setAnswers((current) => {
      const newValues = [...current];
      newValues[index].text = text;
      return newValues;
    });
  };

  const handleChangeLayoutOrientation = (event) => {
    setLayoutOrientation(event.target.value);
  };

  const handleChangeDifficulty = (e) => {
    setQuestionDifficulty(e.target.value);
  };

  const dictionaryToContent = (dictionary) => {
    return Object.keys(dictionary)
      .map((key) => {
        // If the key is text, return the content as is
        if (key === "text") return dictionary[key];
        return `\\${key}(${dictionary[key]})`;
      })
      .join("");
  };

  const handleSave = () => {
    const content = dictionaryToContent(contentDictionary);
    const payload = {
      question,
      content,
      questionNumber,
      difficulty,
      shortAnswers: answers.filter(answer => answer.text.length > 0),
      layoutOrientation,
    };

    onSave(payload);
  };

  const handleAddShortAnswer = () => {
    setAnswers((answers) => {
      if (answers.length >= maxNumberOfAnswers) {
        window.alert(
          `Maximum number of answers reached: ${maxNumberOfAnswers}`
        );
        return answers;
      }
      return [...answers, getSafeAnswer({ text: "" })];
    });
  };

  const handleRemoveShortAnswer = () => {
    setAnswers((answers) => {
      if (answers.length > 1) {
        const confirmed =
          answers.at(-1).text === "" ||
          window.confirm("Are you sure? The last answer text will be lost.");
        if (confirmed) {
          return answers.slice(0, -1);
        }
        // unchanged.
        return answers;
      }

      window.alert("A short question must have at least one short answer.");

      // unchanged.
      return answers;
    });
  };

  return (
    <Layout noBodyPadding>
      <Container>
        <TopContainer>
          <Title> Short Answer Question </Title>
          {(onNext || onPrevious) && showButton ? (
            <Flex
              justifyContent="space-between"
              style={{ gap: 12 }}
              alignItems="center"
            >
              {onPrevious && (
                <Button onClick={onPrevious} size="s" noIcon>
                  Previous Question
                </Button>
              )}
              {onNext && (
                <Button onClick={onNext} size="s" noIcon>
                  Next Question
                </Button>
              )}
            </Flex>
          ) : null}
          <Flex justifyContent="flex-end">
            <DeleteButtonContainer onClick={onDelete}>
              {showButton && (
                <>
                  <FaTrash />
                  <DeleteButton>Delete</DeleteButton>
                </>
              )}
            </DeleteButtonContainer>
            <CancelButton
              onClick={onCancel}
              size="s"
              style={{ border: "none" }}
            >
              Cancel
            </CancelButton>

            <SaveButton onClick={handleSave} size="s" noIcon>
              Save
            </SaveButton>
          </Flex>
        </TopContainer>
        <Flex mb={3} px="5%" py="35px">
          <Box flexGrow={1}>
            <ContentContainer>
              <InputGroup>
                <Label>Question</Label>
                <QuestionField
                  rows={2}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />
                <div className="links flexStart">
                  <Link
                    anchor
                    href={ClassedUpMarkupGuide}
                    name="markup-guide"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Markup guide
                  </Link>
                </div>
              </InputGroup>
              {layoutOrientation === "vert" && (
                <QuestionVerticalLayout
                  answers={answers}
                  onChangeAnswerText={handleChangeAnswerText}
                  orientation={layoutOrientation}
                  updateContent={updateContent}
                  removeContent={removeContent}
                  getContent={getContent}
                  contentDictionary={contentDictionary}
                  onAddShortAnswer={handleAddShortAnswer}
                  onRemoveShortAnswer={handleRemoveShortAnswer}
                />
              )}
              {layoutOrientation === "horiz" && (
                <QuestionHorizontalLayout
                  answers={answers}
                  onChangeAnswerText={handleChangeAnswerText}
                  orientation={layoutOrientation}
                  updateContent={updateContent}
                  removeContent={removeContent}
                  getContent={getContent}
                  contentDictionary={contentDictionary}
                  onAddShortAnswer={handleAddShortAnswer}
                  onRemoveShortAnswer={handleRemoveShortAnswer}
                />
              )}
            </ContentContainer>
          </Box>
          <ExtraOptionsSidebar>
            <ExtraOptions>
              <LabelLeft>Layout Display</LabelLeft>
              <Radio
                type="radio"
                name="layoutOrientation-vertical"
                value="vert"
                onChange={handleChangeLayoutOrientation}
                checked={layoutOrientation === "vert"}
              />
              {" Vertical "}
              <Radio
                type="radio"
                name="layoutOrientation-horizontal"
                value="horiz"
                onChange={handleChangeLayoutOrientation}
                checked={layoutOrientation === "horiz"}
              />
              {" Horizontal "}
              <LabelLeft>Question Difficulty Level</LabelLeft>
              <Select
                id="difficulty"
                name="difficulty"
                value={difficulty}
                onChange={handleChangeDifficulty}
              >
                <option key="-" value="">
                  {"Level"}
                </option>
                {difficulties.map((difficultyOption) => (
                  <option key={difficultyOption} value={difficultyOption}>
                    {difficultyOption}
                  </option>
                ))}
              </Select>
            </ExtraOptions>
          </ExtraOptionsSidebar>
        </Flex>
      </Container>
    </Layout>
  );
};

/**
 * @param {ShortAnswer[]} initialAnswers
 * @param {ShortAnswer[]} defaultAnswers
 * @param {number} maxNumberOfAnswers
 * @returns {ShortAnswer[]}
 */
function getSafeAnswers(initialAnswers, defaultAnswers, maxNumberOfAnswers) {
  if (initialAnswers?.length > maxNumberOfAnswers) {
    // eslint-disable-next-line no-console
    console.warn(`Maximum number of answers exceeded, truncating answers`, {
      answers: initialAnswers,
    });
  }

  return [...(initialAnswers || []), ...defaultAnswers]
    .slice(0, maxNumberOfAnswers)
    .map(getSafeAnswer);
}

/**
 * @param {ShortAnswer} answer
 * @returns {ShortAnswer}
 */
function getSafeAnswer(answer) {
  let safeAnswer = {};
  if (typeof answer?.text === "string") {
    safeAnswer.text = answer.text;
  } else {
    // eslint-disable-next-line no-console
    console.warn("Invalid answer:", { answer });
  }
  return safeAnswer;
}

TestLibraryQuestionsShortAnswer.propTypes = {
  difficulties: PropTypes.arrayOf(PropTypes.string),
  questionNumber: PropTypes.string,
};

export default TestLibraryQuestionsShortAnswer;
